import { QueryKey } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { ReactNode, useMemo } from "react";

import { IAuditLog } from "../../../../@types/AuditLog";
import { TableSorted } from "../../../../components/TableSorted";
import { useModal } from "../../../../hooks/useModal";
import { AUDIT_LOG_PAGE_SIZE_OPTIONS } from "../../../../services/admin/auditLogService";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { isForbidden, serverErrorMessage } from "../../../../utils/HttpError";
import {
  EmailLink,
  ErrorMessage,
  PanelContainer,
  PanelHeader,
  PanelTitle,
} from "../AdminPage.styles";
import { TableContainer } from "../TableContainer";
import { ITablePaginationState, TablePagination } from "../TablePagination";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { describeAuditAction } from "./auditLogAction";
import { AuditLogDetailModal } from "./AuditLogDetailModal";

/** An audit row with Retool's computed "Action" message folded in. */
type IAuditLogRow = IAuditLog & { action: string };

export interface IAuditLogTableProps {
  /** The page on screen: every audit read is paged server-side (EN-5288). */
  logs: IAuditLog[];
  pagination: ITablePaginationState;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading?: boolean;
  isError?: boolean;
  /** Used to tell a 403 apart from a generic failure. */
  error?: unknown;
  title?: string;
  /** Filter bar shown above the table (`AuditLogPanelFilters`). */
  filters?: ReactNode;
  /** Key prefixes of the query that loads `logs`, for the table's Refresh. */
  refreshKeys?: readonly QueryKey[];
  /**
   * The rows came from `/Retool/AuditLogs`, which leaves the before/after
   * snapshots out, so the detail modal fetches the full row.
   */
  loadDetails?: boolean;
}

/**
 * The audit table itself — one server-side page, the computed Action column,
 * and a row drill-down into what changed.
 *
 * Split out of `AuditLogPanel` for EN-5164: the survey answer history reads a
 * different endpoint (`/Retool/Surveys/SurveyAnswers/{id}/AuditLogs`) but
 * wants exactly this presentation, and a second copy of the table, the detail
 * modal and `describeAuditAction` would be three things to keep in step.
 * `AuditLogPanel` is now the entity-scoped data wrapper around this.
 */
export function AuditLogTable({
  logs,
  pagination,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  isError = false,
  error,
  title,
  filters,
  refreshKeys,
  loadDetails = false,
}: IAuditLogTableProps) {
  const { openModal, closeModal } = useModal();

  const rows: IAuditLogRow[] = useMemo(
    () => logs.map((log) => ({ ...log, action: describeAuditAction(log) })),
    [logs]
  );

  const handleRowClick = (auditLog: IAuditLog) => {
    openModal(
      <AuditLogDetailModal
        auditLog={auditLog}
        loadDetails={loadDetails}
        onClose={closeModal}
      />,
      {
        width: "min(900px, 92vw)",
        maxHeight: "90vh",
        showCloseButton: true,
      }
    );
  };

  const columns: ColumnDef<IAuditLogRow, string>[] = useMemo(
    () => [
      {
        accessorKey: "timestamp",
        header: "Timestamp",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.timestamp, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "applicationUserEmail",
        header: "User email",
        cell: ({ row }) =>
          row.original.applicationUserEmail ? (
            <EmailLink
              href={`mailto:${row.original.applicationUserEmail}`}
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.applicationUserEmail}
            </EmailLink>
          ) : (
            <span>-</span>
          ),
        enableSorting: true,
      },
      {
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => <span>{row.original.action}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "source",
        header: "Source",
        cell: ({ row }) => <span>{row.original.source || "-"}</span>,
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PanelContainer>
      {title && (
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
        </PanelHeader>
      )}

      {filters}

      {isError && (
        <ErrorMessage>
          {isForbidden(error)
            ? "You don't have access to audit logs in this environment."
            : serverErrorMessage(error) ??
              "Failed to load the audit log. Try refreshing the page."}
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={refreshKeys}>
          <TableSorted
            columns={columns}
            data={rows}
            onRowClick={handleRowClick}
            emptyStateText={
              isLoading ? <TableLoadingState /> : "No audit log entries found"
            }
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "audit-logs",
            columns,
            rows,
            currentPageOnly: true,
          }}
          pagination={pagination}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={AUDIT_LOG_PAGE_SIZE_OPTIONS}
        />
      </TableContainer>
    </PanelContainer>
  );
}
